import FooterSection from "@/components/FooterSection";
import NavBar from "@/components/NavBar";
import RelatedGlossary from "@/components/RelatedGlossary";
import SideSection from "@/components/SideSection";
import Head from "next/head";
import { convert } from "html-to-text";

// 🧹 Convert HTML safely
const sanitizeHtml = (html) => {
  const sanitized = convert(html, {
    wordwrap: 130,
    selectors: [{ selector: "img", format: "skip" }],
  });
  return sanitized.replace(/\s+/g, " ").trim();
};

function Page({ glossaryData, additionalGlossaries, error }) {
  if (error) {
    return (
      <div className="text-white no-grossary-found">No Glossary Found</div>
    );
  }

  return (
    <>
      <Head>
        <title>{glossaryData ? glossaryData.metaTitle : "Loading..."}</title>
        <meta
          name="keywords"
          content={glossaryData ? glossaryData.metaKeyword : "Loading..."}
        />
        <meta
          name="description"
          content={
            glossaryData
              ? sanitizeHtml(glossaryData.metaDescription)
              : "Loading..."
          }
        />
        <meta
          name="author"
          description={
            glossaryData && glossaryData.user
              ? `${glossaryData.user.name} ${glossaryData.user.email}`
              : "Loading..."
          }
        />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      {/* ===== Header Section ===== */}
      <div className="Headbg">
        <div className="container">
          <NavBar />
          <div className="mt-5">
            <h4 className="textGradient fs-3 fw-bold text-center mb-2">
              {glossaryData?.title}
            </h4>
            <p className="text-center text-white fs-5 truncate-lines">
              {glossaryData && sanitizeHtml(glossaryData.description)}
            </p>
          </div>
        </div>
      </div>

      {/* ===== Glossary Detail Section ===== */}
      <div className="container">
        <div className="row">
          <div className="col-xl-9 col-md-12 col-12">
            <div className="costMargin detailsPage">
              {glossaryData && (
                <>
                  <h2 className="textGradient fs-3 fw-bold mb-4">
                    {glossaryData.title}
                  </h2>
                  <div
                    className="text-white fs-5 mb-4"
                    dangerouslySetInnerHTML={{
                      __html: glossaryData.description,
                    }}
                  />
                  <div className="clearfix"></div>
                </>
              )}
              {additionalGlossaries?.length > 0 && (
                <>
                  <h5 className="textGradient fs-4 fw-bold mt-3">
                    Related Glossaries
                  </h5>
                  <RelatedGlossary
                    additionalGlossaries={additionalGlossaries}
                  />
                </>
              )}
            </div>
          </div>

          <div className="col-xl-3 col-md-12 col-12">
            <SideSection />
          </div>
        </div>
      </div>

      <FooterSection />
    </>
  );
}

// ✅ FIXED getServerSideProps
export async function getServerSideProps(context) {
  const { slug } = context.query;
  const targetSlug = String(slug || "").toLowerCase();

  // ✅ Safe base URL: relative path for production (prevents HTTPS self-call)
  // const baseUrl =
  // process.env.NODE_ENV === "development"
  //   ? "http://localhost:3000"
  //   : "http://127.0.0.1:3000";  
  const baseUrl =
  process.env.NODE_ENV === "development"
    ? "http://localhost:3000"
    : process.env.INTERNAL_API_URL || "http://127.0.0.1:3003";

  const slugifyTitle = (text = "") =>
    text
      .toLowerCase()
      .trim()
      .replace(/[\s/\\()]+/g, "-")
      .replace(/[+]+/g, "plus")
      .replace(/&/g, "and")
      .replace(/\./g, "-")
      .replace(/[^a-z0-9-]+/g, "")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");

  try {
    // ✅ Use internal relative API fetch (no SSL self-fetch)
    const listRes = await fetch(`${baseUrl}/api/glossary`);
    
    
    if (!listRes.ok) throw new Error("Failed to fetch glossary list");
    const list = await listRes.json();
    if (!Array.isArray(list)) return { props: { error: true } };

    console.log("🔹 URL slug:", targetSlug);
    console.log("🔹 Available titles:", list.map((g) => g.title));

    // --- Exact match first
    let match = list.find((i) => slugifyTitle(i.title) === targetSlug);

    // --- Fallback: partial & fuzzy match
    if (!match) {
      match = list.find(
        (i) =>
          slugifyTitle(i.title).includes(targetSlug) ||
          targetSlug.includes(slugifyTitle(i.title))
      );
    }

    if (!match) {
      console.warn("⚠️ No match found for slug:", targetSlug);
      return { props: { error: true } };
    }

    // ✅ Fetch detailed glossary info
    const detailsRes = await fetch(
      `${baseUrl}/api/glossary?title=${encodeURIComponent(match.title)}`
    );

    if (detailsRes.ok) {
      const data = await detailsRes.json();
      return {
        props: {
          glossaryData: data.glossary ?? match,
          additionalGlossaries: data.additionalGlossaries ?? [],
          error: false,
        },
      };
    }

    // fallback if no detail endpoint
    return {
      props: {
        glossaryData: match,
        additionalGlossaries: list
          .filter((g) => g.title !== match.title)
          .slice(0, 6),
        error: false,
      },
    };
  } catch (err) {
    console.error("❌ Glossary detail error:", err);
    return { props: { error: true } };
  }
}

export default Page;
